import React, { useCallback, useMemo, useState, useEffect } from 'react'
import DataTable from 'components/InfiniteTable/DataTable'
import { useDispatch, useSelector } from 'react-redux'
import { getItems, deleteItem } from '../../../../../redux/actions/item'
import { useUser } from '../../../../../contexts/UserContext'
import { useGlobal } from '../../../../../contexts'
import axios from '../../../../../axios'
import {
  Card,
  IconButton,
  CardBody,
  Switch,
  useDisclosure,
  Text,
  useColorModeValue,
  useColorMode,
  Box,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  Modal,
  ModalFooter,
  Flex,
  SimpleGrid,
  FormLabel,
  Button,
  Badge
} from '@chakra-ui/react'
import { SquarePen, Trash, Eye, Album, ListFilter, RefreshCw } from 'lucide-react'
import { getSaleInvoices } from '../../../../../redux/actions/salesInvoice'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import SalesDetails from 'components/salesDetails/SalesDetails';
import { getSingleWithPrevAndNext, deleteSaleInvoice } from "../../../../../redux/actions/salesInvoice"
import { deleteRecordForSales } from "../../../../../utils/util"
import SalesInvoiceFilterModal from "../../../sales/Pages/SalesInvoiceMaster/SalesInvoiceFilterModal"
import { postColumnsHideShow } from "../../../../../redux/actions/utility"


const ColumnVisibilityModal = ({ isOpen, onClose, columnVisibility, setColumnVisibility, user }) => {
  const [localVisibility, setLocalVisibility] = useState(columnVisibility);
  const companyId = Number(localStorage.getItem('companyId'));
  const dispatch = useDispatch();

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  useEffect(() => {
    if (isOpen) {
      setLocalVisibility(columnVisibility);
    }
  }, [isOpen, columnVisibility]);

  const handleToggle = (key) => {
    setLocalVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    try {
      const updatedViewJson = user.viewJson
        ? user.viewJson?.map((item) =>
          item.companyId === companyId ? { ...item, sales: localVisibility } : item,
        )
        : [{ companyId, sales: localVisibility }];

      await dispatch(
        postColumnsHideShow({
          userId: user?.id,
          viewJson: updatedViewJson,
        }),
      );

      setColumnVisibility(localVisibility);
      localStorage.setItem('salesInvoiceColumnVisibility', JSON.stringify(localVisibility));
      onClose();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent
        bg={
          isDark
            ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 50%, #1e293b 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%)'
        }
        border={isDark ? '1px solid #334155' : '1px solid #e2e8f0'}>
        <ModalHeader
          borderBottom={isDark ? '1px solid #334155' : '1px solid #e2e8f0'}
          bg={isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.5)'}>
          <Flex justify="space-between" align="center">
            <Box>
              <Text color={isDark ? 'white' : 'gray.900'} fontSize="2xl" fontWeight="bold">
                Show/Hide Columns
              </Text>
              <Text color={isDark ? 'gray.400' : 'gray.600'} fontSize="sm" mt={1}>
                Customize which columns appear in your sales invoice table
              </Text>
            </Box>
          </Flex>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody maxH="60vh" overflowY="auto" py={6}>
          <SimpleGrid columns={2} spacing={4}>
            {Object.entries(localVisibility)?.map(([key, value]) => (
              <Card
                key={key}
                p={5}
                cursor="pointer"
                border="2px solid"
                borderColor={value ? (isDark ? 'blue.500' : 'blue.400') : isDark ? 'gray.600' : 'gray.300'}
                bg={value ? (isDark ? 'blue.900/20' : 'blue.50/50') : isDark ? 'gray.800/30' : 'white/50'}
                _hover={{ transform: 'scale(1.02)' }}
                transition="all 0.3s"
                onClick={() => handleToggle(key)}>
                <Flex justify="space-between" align="center">
                  <FormLabel
                    htmlFor={key}
                    mb={0}
                    fontWeight="semibold"
                    color={isDark ? 'white' : 'gray.900'}
                    cursor="pointer">
                    {key.split(/(?=[A-Z])/).join(' ')}
                  </FormLabel>
                  <Switch
                    id={key}
                    isChecked={value}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleToggle(key);
                    }}
                    colorScheme="blue"
                    size="lg"
                  />
                </Flex>
                <Flex align="center" gap={2} mt={3}>
                  <Box
                    w={2}
                    h={2}
                    borderRadius="full"
                    bg={value ? (isDark ? 'blue.400' : 'blue.500') : isDark ? 'gray.600' : 'gray.400'}
                  />
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    color={value ? (isDark ? 'blue.400' : 'blue.600') : isDark ? 'gray.500' : 'gray.500'}>
                    {value ? 'Visible' : 'Hidden'}
                  </Text>
                </Flex>
              </Card>
            ))}
          </SimpleGrid>
        </ModalBody>

        <ModalFooter
          borderTop={isDark ? '1px solid #334155' : '1px solid #e2e8f0'}
          bg={isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.5)'}>
          <Button
            variant="ghost"
            mr={3}
            rounded={'md'}
            onClick={onClose}
            color={isDark ? 'gray.300' : 'gray.700'}
            _hover={{ bg: isDark ? 'gray.700' : 'gray.200' }}>
            Cancel
          </Button>
          <Button
            className={`
              !px-6 !py-2.5 !rounded-lg !font-semibold !transition-all !duration-200 !shadow-lg
              ${isDark
                ? '!bg-gradient-to-r !from-blue-600 !to-indigo-600 hover:!from-blue-500 hover:!to-indigo-500'
                : '!bg-gradient-to-r !from-blue-500 !to-indigo-500 hover:!from-blue-600 hover:!to-indigo-600'
              }
              !text-white
              ${isDark ? '!shadow-blue-500/30' : '!shadow-blue-400/30'}
              hover:!shadow-xl
              hover:!-translate-y-1
              active:!translate-y-0
            `}
            onClick={handleSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default function NewItem() {
  const dispatch = useDispatch()
  const { colorMode } = useColorMode();
  const { user } = useUser()
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();
  const { userGlobalSettingsData, company } = useGlobal()
  const globalSetting = userGlobalSettingsData?.[0]?.askCompany?.item
  const shouldShowCompanyDropdown = globalSetting || false
  const salesInvoiceData = useSelector((state) => state.sales.list);
  const pagination = useSelector((state) => state.sales?.pagination);
  const totalCount = useSelector((state) => state.sales?.totalCount);
  const Totals = useSelector((state) => state.sales.pagination.totals);
  const daybookData = useSelector((state) => state.daybook?.list || []);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [singleEntity, setSingleEntity] = useState(null);
  const [paginateData, setPaginateData] = useState({});
  const [isOpenSingleView, setIsOpenSingleView] = useState(false);
  const viewData = singleEntity;
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [data, setData] = useState([]);
  const [dataId, setDataId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    // pendingstatus: 'all',
    // invoiceBook: 'all',
    // duration: 'any',
    // contact: '',
    // item: '',
    // docno: '',
    // daybookIds: [],
    // accountIds: [],
    // accountId: '',
    // fromDate: '',
    // toDate: '',
    sortBy: 'id',
    sortOrder: 'DESC',
  });
  const [isRefresh, setIsRefresh] = useState(false);

  const DEFAULT_SALES_COLUMNS = {
    docno: true,
    partyNo: false,
    transactionAt: true,
    itemname: true,
    // ...(mobile && { serialno: true }),
    daybookname: true,
    accountname: true,
    qty1: false,
    qty2: false,
    qty3: false,
    taxableAmount: true,
    gstAmount: true,
    netAmount: true,
    pendingamt: true,
    dueAt: false,
    dueDayCount: false,
    dueDays: false,
    txnDayCount: false,
    pendingstatus: true,
    createdu: false,
    updatedu: false,
    status: false,
    action: true,
  };

  const [columnVisibility, setColumnVisibility] = useState(() => {
    // First, check if we have saved visibility in localStorage
    const savedVisibility = localStorage.getItem('salesInvoiceColumnVisibility');
    if (savedVisibility) {
      try {
        const parsed = JSON.parse(savedVisibility);
        // Merge with defaults to ensure any new columns are included
        return { ...DEFAULT_SALES_COLUMNS, ...parsed };
      } catch (error) {
        console.error('Error parsing saved column visibility:', error);
      }
    }

    // If no saved visibility, check user settings
    if (user?.viewJson?.length && user.viewJson[0]?.sales) {
      return { ...DEFAULT_SALES_COLUMNS, ...user.viewJson[0].sales };
    }

    // Fallback to defaults
    return DEFAULT_SALES_COLUMNS;
  });

  const isDark = colorMode === 'dark';
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const bg = useColorModeValue('#f2f7f2', '#1a254b');
  const icon = useColorModeValue('#000000', '#ffffff');
  const hoverBg = useColorModeValue('gray.200', 'gray.600');

  // Selection state
  const [selectedRows, setSelectedRows] = useState([])
  // Search state
  const [search, setSearch] = useState('')

  // Handle select all
  const handleSelectAll = useCallback((data) => {
    if (selectedRows.length === data.length) {
      setSelectedRows([])
    } else {
      setSelectedRows(data?.map(item => item.id))
    }
  }, [selectedRows])

  // Handle single row selection
  const handleRowSelect = useCallback((id) => {
    setSelectedRows(prev => {
      if (prev.includes(id)) {
        return prev.filter(rowId => rowId !== id)
      }
      return [...prev, id]
    })
  }, [])

  // Handle bulk delete
  const handleBulkDelete = useCallback(async () => {
    if (selectedRows.length === 0) return

    if (window.confirm(`Are you sure you want to delete ${selectedRows.length} item(s)?`)) {
      try {
        // Delete each selected item
        for (const id of selectedRows) {
          await dispatch(deleteItem(id, user?.loginhistory?.companyId))
        }
        setSelectedRows([])
        // Refresh the table data by triggering a re-fetch
        window.location.reload()
      } catch (error) {
        console.error('Error deleting items:', error)
      }
    }
  }, [selectedRows, dispatch, user?.loginhistory?.companyId])

  const handlePrevPage = useCallback(
    async (page) => {
      setIsModalLoading(true);
      try {
        await dispatch(
          getSingleWithPrevAndNext({
            page: page,
            limit: 1,
            sortBy: 'id',
            sortOrder: 'desc',
          }),
        );
      } finally {
        setIsModalLoading(false);
      }
    },
    [dispatch],
  );

  const handleNextPage = useCallback(
    async (page) => {
      setIsModalLoading(true);
      try {
        await dispatch(
          getSingleWithPrevAndNext({
            page: page,
            limit: 1,
            sortBy: 'id',
            sortOrder: 'desc',
          }),
        );
      } finally {
        setIsModalLoading(false);
      }
    },
    [dispatch],
  );

  const handleViewSingleData = async (data, action = 'new') => {
    setIsModalLoading(true); // Use separate state
    onOpen(); // Open modal immediately

    try {
      if (action === 'update') {
        const res = await dispatch(
          getSingleWithPrevAndNext({
            id: data.id,
            page: 1,
            limit: 1,
            sortBy: 'id',
            sortOrder: 'desc',
          }),
        );
        setSingleEntity(res?.[0]);
      } else {
        setSingleEntity(data);
      }
      // await dispatch(getSaleInvoice(id));
      const currentIdx = salesInvoiceData.findIndex((x) => x.id === data.id);

      const prevObject = currentIdx > 0 ? salesInvoiceData[currentIdx - 1] : null;
      const nextObject = currentIdx < salesInvoiceData.length - 1 ? salesInvoiceData[currentIdx + 1] : null;

      // await dispatch(
      //    getSingleWithPrevAndNext({
      //       id: id,
      //       page: 1,
      //       limit: 1,
      //       sortBy: 'id',
      //       sortOrder: 'desc',
      //    }),
      // );
      setPaginateData({
        hasPreviousPage: prevObject ? true : false,
        previousPage: prevObject ? currentIdx - 1 : null,
        nextPage: nextObject ? currentIdx + 1 : null,
        hasNextPage: nextObject ? true : false,
      });
      setIsOpenSingleView(true);
    } catch (error) {
      console.error('Error fetching sales data:', error);
    } finally {
      setIsModalLoading(false); // Update separate state
    }
  };

  const deleteSales = () => {
    const selectedData = data.filter((da) => da.id === viewDetail?.id);
    const selectedDaybook = daybookData.filter((dd) => dd.id === selectedData[0].daybookId);
    if (selectedDaybook.length > 0 && selectedDaybook[0]?.askPasswordOnDelete) {
      setDataId({ id: viewDetail?.id, daybookId: viewDetail.daybookId, accountId: viewDetail.accountId });
      setIsModalOpen(true);
      onClose();
    } else {
      deleteRecordForSales(
        viewDetail?.id,
        viewDetail.daybookId,
        viewDetail.accountId,
        deleteSaleInvoice,
        dispatch,
        user?.loginhistory?.companyId,
        userGlobalSettingsData,
      );
      onClose();
    }
  };



  const handleApplyFilters = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      sortBy: 'id',
      sortOrder: 'DESC',
    }));
  };

  const handleRefreshData = async () => {
    setIsRotating(!isRotating);
    setFilters((prev) => ({
      ...prev,
      sortBy: 'id',
      sortOrder: 'DESC',
    }));
    setIsRefresh((prev) => !prev);
  };

  const columns = useMemo(() => {
    return [
      {
        id: 'select',
        header: ({ table }) => {
          const data = table.options.data
          return (
            <input
              type="checkbox"
              checked={selectedRows.length === data.length && data.length > 0}
              onChange={() => handleSelectAll(data)}
              className="w-4 h-4 cursor-pointer"
            />
          )
        },
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRows.includes(row.original.id)}
            onChange={() => handleRowSelect(row.original.id)}
            className="w-4 h-4 cursor-pointer"
          />
        ),
        size: 50,
      },
      {
        id: "actions",
        header: "Actions",
        sticky: "right",
        cell: ({ row }) => (
          <div className="flex gap-2 justify-start items-center">
            <IconButton
              colorScheme='green'
              aria-label='Edit' rounded={8} size='sm' variant='outline'
              icon={<SquarePen size={18} />}
              onClick={() => navigate(`/admin/sales/invoice/edit/${row.original.id}`)}
            />
            <IconButton
              colorScheme='red'
              aria-label='Delete' rounded={8} size='sm' variant='outline'
              icon={<Trash size={18} />}
            />
          </div >
        ),
      },
      {
        id: 'docno',
        header: 'DOC NO',
        // accessorKey: 'docno',
        size: 100,
        cell: ({ row }) => (
          <div
            onClick={() => {
              onOpen()
              handleViewSingleData(row.original)
            }}
            className="cursor-pointer hover:text-blue-600"
          >
            {row.original.docno}
          </div>
        ),
      },
      {
        id: 'partyNo',
        header: 'Party No',
        accessorKey: 'partyNo',
      },
      {
        id: 'transactionAt',
        header: 'BILL DATE',
        accessorKey: 'transactionAt',
      },
      {
        id: 'txnDayCount',
        header: 'Bill Days Count',
        accessorKey: 'txnDayCount',
      },
      {
        id: 'daybookname',
        header: 'Daybook Name',
        accessorKey: 'daybookname',
      },
      {
        id: 'accountname',
        header: 'PARTY NAME',
        accessorKey: 'accountname',
        size: 200
      },
      // {
      //   id: 'qty1',
      //   header: 'QTY 1',
      //   accessorKey: 'qty1',
      // },
      // {
      //   id: 'qty2',
      //   header: 'QTY 2',
      //   accessorKey: 'qty2',
      // },
      // {
      //   id: 'qty3',
      //   header: 'QTY 3',
      //   accessorKey: 'qty3',
      // },
      {
        id: 'taxableAmount',
        header: 'Taxable Amount',
        // accessorKey: 'taxableAmount',
        cell: ({ row }) => {
          return row.original?.lineitems?.reduce((acc, item) => acc + item.taxableAmount, 0)
        }
      },
      {
        id: 'gstAmount',
        header: 'GST Amount',
        // accessorKey: 'gstAmount',
        cell: ({ row }) => {
          return row.original?.lineitems?.reduce((acc, item) => acc + item.netGst, 0) || 0
        }
      },
      {
        id: 'pendingamt',
        header: 'Pending Amount',
        accessorKey: 'pendingamt',
      },
      {
        id: 'dueAt', header: 'Due At', cell: ({ row }) => {
          return moment(row.original.dueAt).format('DD/MM/YYYY')
        }
      },
      // { id: 'dueDays', header: 'Due Days', accessorKey: 'dueDays' },
      // { id: 'dueDayCount', header: 'Over Due Days', accessorKey: 'dueDayCount' },
      {
        id: 'pendingstatus',
        header: 'Payment Status',
        // accessorKey: 'pendingstatus',
        cell: ({ row }) => {
          const borderColor = row.original?.pendingstatus === 'CLEAR' ? 'green.400' : row.original?.pendingstatus === 'FULL PENDING' ? 'orange.400' : 'blue.400'
          const badgeColor = row.original?.pendingstatus === 'CLEAR' ? 'green.400' : row.original?.pendingstatus === 'FULL PENDING' ? 'orange.400' : 'blue.400'
          return (
            <Badge
              border="1px solid"
              bg="transparent"
              borderColor={borderColor}
              color={badgeColor}
              borderRadius="md"
              w="100%"
              textAlign="center"
              textTransform="capitalize"
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {row.original?.pendingstatus}
            </Badge>
          )
        },
        size: 200
      },
      { id: 'createdu', header: 'Created By', accessorKey: 'createdu' },
      { id: 'updatedu', header: 'Updated By', accessorKey: 'updatedu' },
      {
        id: 'status', header: 'STATUS',
        //accessorKey: 'status' 
        cell: ({ row }) => {
          return (<Switch isChecked={row.original?.status} />)
        }
      }
    ]
  }, [selectedRows, handleSelectAll, handleRowSelect])

  const saleInvoiceRight = useMemo(() => {
    if (user?.userRights?.length > 0 && user?.loginhistory?.companyId) {
      return user?.userRights?.find((right) => right.companyId === user?.loginhistory?.companyId)?.saleinvoice;
    }
    return null;
  }, [user?.userRights, user?.loginhistory?.companyId]);

  const { viewDetail, paginate } = useMemo(() => {
    const data = viewData;
    const pagination = viewData?.pagination;
    return { viewDetail: data, paginate: pagination };
  }, [viewData, viewData?.pagination]);

  useEffect(() => {
    localStorage.setItem('salesInvoiceColumnVisibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);


  return (
    <>
      <Card>
        <CardBody>
          <div className="space-y-4">
            {/* Search and Selection Header */}
            {selectedRows.length > 0 ? (
              <div className="flex items-center justify-between flex-1 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-500 text-white rounded-full text-sm font-semibold">
                    {selectedRows.length}
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {selectedRows.length} {selectedRows.length === 1 ? 'item' : 'items'} selected
                  </span>
                </div>
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <Trash size={16} />
                  Delete Selected
                </button>
              </div>
            ) : null}
            <div className="flex items-center justify-between gap-4">
              {/* Selection Header - Left Side */}
              <div><Text
                color={textColor}
                ml={{ base: '8px', md: 0 }}
                fontSize={{ base: '17px', lg: '22px' }}
                fontWeight="700"
                lineHeight="100%">
                Sale Invoice
              </Text></div>

              {/* Search Input - Right Side */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/admin/sales/invoice/add')}
                  className={`group relative flex items-center justify-center gap-3 py-2 px-7 rounded-xl font-medium shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-1 focus:outline-none focus:ring-4 active:scale-95 overflow-hidden border ${isDark
                    ? 'bg-gradient-to-br from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-white shadow-cyan-500/30 hover:shadow-2xl hover:shadow-cyan-400/40 focus:ring-cyan-200/50 border-cyan-300/30'
                    : 'bg-gradient-to-br from-emerald-400 to-teal-500 hover:from-emerald-300 hover:to-teal-400 text-white shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-400/40 focus:ring-emerald-200/70 border-emerald-300/50'
                    }`}>
                  {/* Gradient overlay on hover */}
                  <span
                    className={`absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out ${isDark ? 'via-cyan-100/20' : 'via-white/30'
                      }`}></span>

                  {/* Icon container with background */}
                  <span
                    className={`relative flex items-center justify-center w-6 h-6 rounded-lg backdrop-blur-sm transition-all duration-300 ${isDark
                      ? 'bg-cyan-100/20 group-hover:bg-cyan-100/30'
                      : 'bg-white/20 group-hover:bg-white/30'
                      }`}>
                    <svg
                      className="w-4 h-4 transition-transform duration-300 group-hover:rotate-90"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                    </svg>
                  </span>

                  <span className="relative text-base font-semibold">Add New</span>
                </button>
                <IconButton
                  icon={
                    <Box
                      as={RefreshCw}
                      transform={isRotating ? 'rotate(-360deg)' : 'rotate(0deg)'}
                      transition="transform 1s ease"
                      color="gray"
                    />
                  }
                  aria-label="Refresh"
                  rounded={8}
                  onClick={handleRefreshData}
                />
                <IconButton aria-label='Filter' icon={<ListFilter color="gray" />} rounded={8} onClick={onFilterOpen} />
                <IconButton aria-label='Daybook' icon={<Album color="gray" />} rounded={8} onClick={() => navigate('/admin/sales/invoice/managedaybook')} />
                <IconButton aria-label='Column visibility' icon={<Eye color="gray" />} rounded={8} onClick={() => setIsColumnModalOpen(true)} />
                <div className="relative w-80">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm 
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                     transition-all duration-200 bg-white shadow-sm hover:border-gray-400"
                  />
                </div>
              </div>
            </div>

            <DataTable columns={columns} pageSize={50} fetchData={getSaleInvoices}
              extraParams={{
                ...(filters.paymentstatus !== 'all' && { paymentStatus: filters.paymentstatus }),
                ...(filters.invoiceBook !== 'all' && { invoiceBook: filters.invoiceBook }),
                ...(filters.duration !== 'any' && { duration: filters.duration }),
                ...(filters.contact && { contact: filters.contact }),
                ...(filters.item && { item: filters.item }),
                ...(filters.docno && { docno: filters.docno }),
                ...(filters.daybookIds && { daybookIds: filters.daybookIds }),
                ...(filters.accountIds && { accountIds: filters.accountIds }),
                ...(filters.accountId && { accountId: filters.accountId }),
                ...(filters.fromDate && { fromDate: filters.fromDate }),
                ...(filters.toDate && { toDate: filters.toDate }), sortBy: "id", sortOrder: "DESC", search
              }}
              columnVisibility={columnVisibility}
              isRefresh={isRefresh}
            />
          </div>
        </CardBody>
      </Card>

      {isOpen && (
        <SalesDetails
          singleEntityRights={saleInvoiceRight}
          isOpen={isOpen}
          onClose={onClose}
          title="Sales Invoice"
          handlePrevPage={handlePrevPage}
          handleViewSingleData={handleViewSingleData}
          handleNextPage={handleNextPage}
          paginate={paginateData}
          totalCount={totalCount}
          viewDetail={viewDetail}
          entityType="saleinvoice"
          singleEntity={singleEntity}
          onEditFormClose={() => setIsOpenSingleView(true)}
          setIsOpenSingleView={setIsOpenSingleView}
          isEditFormOpen={isEditFormOpen}
          setIsEditFormOpen={setIsEditFormOpen}
          lineItems={viewDetail?.lineitems}
          billTerms={viewDetail?.salebillterms}
          onDelete={deleteSales}
          handleSerchClose={() => console.log('')}
        />
      )}

      {/* Filter Modal */}
      <SalesInvoiceFilterModal
        isOpen={isFilterOpen}
        onClose={onFilterClose}
        onApplyFilters={handleApplyFilters}
        initialFilters={filters}
        entityType="saleinvoice"
        Totals={Totals}
      />

      {/* Column Visibility Modal */}
      <ColumnVisibilityModal
        isOpen={isColumnModalOpen}
        onClose={() => setIsColumnModalOpen(false)}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        user={user}
      />
    </>
  )
}
